// Apolo IDE — Live TodoWrite list (Epic 13).
//
// Sits at the top of the Tasks tab. Reads ide_todos for the active code
// session and renders a checklist with status pills. Clicking the icon
// cycles status pending -> in_progress -> completed via PATCH.

import { useCallback, useEffect, useState } from 'react';
import { Circle, CircleDot, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { apiFetch } from '../../../lib/api.js';
import { useAgentStore } from '../../../store/useAgentStore.js';

const STATUS_TONE = {
  pending: 'text-apolo-textTertiary',
  in_progress: 'text-apolo-accent',
  completed: 'text-apolo-success',
};

function StatusIcon({ status, ...rest }) {
  if (status === 'completed') return <CheckCircle2 size={14} {...rest} />;
  if (status === 'in_progress') return <CircleDot size={14} {...rest} />;
  return <Circle size={14} {...rest} />;
}

function nextStatus(current) {
  if (current === 'pending') return 'in_progress';
  if (current === 'in_progress') return 'completed';
  return 'pending';
}

export default function TodosLiveList() {
  const activeSessionId = useAgentStore((s) => s.code.activeSessionId);
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!activeSessionId) return;
    setLoading(true);
    setError(null);
    const res = await apiFetch(`/api/code-ide/sessions/${activeSessionId}/todos`);
    setLoading(false);
    if (!res.ok) {
      setError(res.error || 'falha');
      return;
    }
    setTodos(res.data.todos ?? []);
  }, [activeSessionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function cycleStatus(todo) {
    const status = nextStatus(todo.status);
    const res = await apiFetch(
      `/api/code-ide/sessions/${activeSessionId}/todos/${todo.id}`,
      { method: 'PATCH', body: { status } },
    );
    if (res.ok) {
      setTodos((prev) => prev.map((t) => (t.id === todo.id ? res.data.todo : t)));
    }
  }

  if (!activeSessionId) return null;

  return (
    <section className="flex flex-col gap-2">
      <header className="flex items-center justify-between text-xs uppercase text-apolo-textTertiary">
        <span>Todos</span>
        <button
          type="button"
          onClick={refresh}
          aria-label="Atualizar todos"
          className="rounded-md p-1 text-apolo-textTertiary transition hover:text-apolo-textPrimary"
        >
          {loading ? (
            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw size={12} aria-hidden="true" />
          )}
        </button>
      </header>

      {error && (
        <p role="alert" className="text-xs text-apolo-error">
          {error}
        </p>
      )}

      {todos.length === 0 && (
        <p className="text-xs text-apolo-textTertiary">
          Sem todos ainda — o agente vai criar via TodoWrite.
        </p>
      )}

      <ol className="flex flex-col gap-1">
        {todos.map((todo) => (
          <li
            key={todo.id}
            className="flex items-start gap-2 rounded-md border border-apolo-border bg-apolo-surface2 px-2 py-1.5 text-xs"
          >
            <button
              type="button"
              onClick={() => cycleStatus(todo)}
              aria-label={`Status: ${todo.status} — clique para avançar`}
              className="mt-px shrink-0"
            >
              <StatusIcon
                status={todo.status}
                aria-hidden="true"
                className={STATUS_TONE[todo.status] || STATUS_TONE.pending}
              />
            </button>
            <span
              className={
                todo.status === 'completed'
                  ? 'text-apolo-textTertiary line-through'
                  : 'text-apolo-textPrimary'
              }
            >
              {todo.text}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}
